import { useEffect, useRef, useState } from 'react';
import { COLORS } from '../../constants/theme';
import { Spinner } from '../Spinner';
import { ErrorMessage } from '../ErrorMessage';
import { formatNumber, formatEnumLabel } from '../../utils/format';
import { sortTargetGroups, TARGET_SORT_OPTIONS } from '../../utils/intelSort';
import type { TargetSortValue } from '../../utils/intelSort';
import { deriveHeadlineStats } from '../../utils/groupIntelByTarget';
import { IntelFreshnessIndicator } from '../attack/IntelFreshnessIndicator';
import { IntelDetailModal } from './IntelDetailModal';
import { INTEL_TYPE_META } from './intelTypeMeta';
import type { IntelHubShellProps } from './IntelHubTab';
import { PAGE_SIZE } from './IntelHubTab';
import type { IntelType } from '../../types/intel';

/**
 * SF-157 Component 7: the existing Intel Hub table is re-architected to group by target, not left
 * flat — the same target-scouting reframing applies to both platforms. A primary-row click opens
 * `IntelDetailModal` stacked by type for that target's reports (reusing the existing modal
 * component rather than expandable nested rows).
 */
export function DesktopIntelHubShell(props: IntelHubShellProps) {
  const {
    targetGroups, crystalEyeReports, sortKey, onSortKey, scope, onScope,
    loading, error, totalCount, handleShare, handleDelete, onRefresh, page, onPage,
  } = props;

  const h1Ref = useRef<HTMLHeadingElement>(null);
  const [openTargetId, setOpenTargetId] = useState<string | null>(null);

  useEffect(() => {
    h1Ref.current?.focus();
  }, []);

  const isBlockingLoad = loading && targetGroups.length === 0 && crystalEyeReports.length === 0;
  const isBlockingError = Boolean(error) && targetGroups.length === 0 && crystalEyeReports.length === 0;

  const sorted = sortTargetGroups(targetGroups, sortKey);
  const openGroup = targetGroups.find((g) => g.targetProvinceId === openTargetId);
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <div style={{ padding: '16px' }}>
      <h1
        ref={h1Ref}
        tabIndex={-1}
        style={{ margin: '0 0 12px', color: COLORS.text.primary, fontSize: 20, fontWeight: 700, outline: 'none' }}
      >
        Intel Hub
      </h1>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', alignItems: 'center' }}>
        <button
          onClick={() => onScope('mine')}
          style={{
            backgroundColor: scope === 'mine' ? COLORS.link : 'transparent',
            border: `1px solid ${scope === 'mine' ? COLORS.link : COLORS.border}`,
            borderRadius: '4px',
            color: scope === 'mine' ? '#fff' : COLORS.text.secondary,
            padding: '4px 12px',
            fontSize: '12px',
            cursor: 'pointer',
          }}
        >
          My Intel
        </button>
        <button
          onClick={() => onScope('kingdom')}
          style={{
            backgroundColor: scope === 'kingdom' ? COLORS.link : 'transparent',
            border: `1px solid ${scope === 'kingdom' ? COLORS.link : COLORS.border}`,
            borderRadius: '4px',
            color: scope === 'kingdom' ? '#fff' : COLORS.text.secondary,
            padding: '4px 12px',
            fontSize: '12px',
            cursor: 'pointer',
          }}
        >
          Kingdom Intel
        </button>
        {!loading && (
          <button
            onClick={onRefresh}
            style={{
              backgroundColor: 'transparent',
              border: `1px solid ${COLORS.border}`,
              borderRadius: '4px',
              color: COLORS.text.muted,
              padding: '4px 12px',
              fontSize: '12px',
              cursor: 'pointer',
            }}
          >
            Refresh
          </button>
        )}

        <label style={{ marginLeft: 'auto', fontSize: 12, color: COLORS.text.tertiary, display: 'flex', alignItems: 'center', gap: 6 }}>
          Sort
          <select
            aria-label="Sort scouted targets by"
            value={sortKey}
            onChange={(e) => onSortKey(e.target.value as TargetSortValue)}
            style={{
              background: COLORS.cardAlt, border: `1px solid ${COLORS.border}`,
              borderRadius: 4, color: COLORS.text.primary, fontSize: 12, padding: '4px 8px',
            }}
          >
            {TARGET_SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </label>
      </div>

      {error && !isBlockingError && (
        <div style={{ color: COLORS.negative, fontSize: '12px', marginBottom: '8px' }}>{error}</div>
      )}

      {isBlockingLoad && <Spinner />}
      {isBlockingError && <ErrorMessage message={error!} />}

      {!isBlockingLoad && !isBlockingError && (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ backgroundColor: COLORS.sectionHeader }}>
                  <th style={thStyle}>Target</th>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Race</th>
                  <th style={thStyle}>Networth</th>
                  <th style={thStyle}>Land</th>
                  <th style={thStyle}>Offense</th>
                  <th style={thStyle}>Defense</th>
                  <th style={thStyle}>Captured</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((group, idx) => {
                  const stats = deriveHeadlineStats(group);
                  const presentTypes = Array.from(new Set(group.reports.map((r) => r.type))) as IntelType[];
                  const bg = idx % 2 === 0 ? 'transparent' : COLORS.cardAlt;
                  const cellStyle: React.CSSProperties = {
                    padding: '5px 8px',
                    borderBottom: `1px solid ${COLORS.border}`,
                    fontSize: '12px',
                  };
                  return (
                    <tr
                      key={group.targetProvinceId}
                      style={{ backgroundColor: bg, cursor: 'pointer' }}
                      onClick={() => setOpenTargetId(group.targetProvinceId)}
                    >
                      <td style={cellStyle}>
                        <span style={{ color: COLORS.text.primary }}>{group.targetProvinceName}</span>
                        <span style={{ color: COLORS.text.muted, marginLeft: '4px', fontSize: '11px' }}>
                          ({group.targetKingdomLocation})
                        </span>
                      </td>
                      <td style={cellStyle}>
                        <div style={{ display: 'flex', gap: 3 }}>
                          {presentTypes.map((type) => {
                            const meta = INTEL_TYPE_META[type];
                            return (
                              <span
                                key={type}
                                style={{
                                  display: 'inline-block', padding: '1px 5px', borderRadius: 3,
                                  fontSize: 9, fontWeight: 700, color: meta.fg, background: meta.bg,
                                  border: `1px solid ${meta.bd}`,
                                }}
                              >
                                {meta.label}
                              </span>
                            );
                          })}
                        </div>
                      </td>
                      <td style={cellStyle}>
                        <span style={{ color: COLORS.text.secondary }}>{group.race != null ? formatEnumLabel(group.race) : '—'}</span>
                      </td>
                      <td style={{ ...cellStyle, fontFamily: 'JetBrains Mono, monospace' }}>
                        {stats.networth != null ? (
                          <span style={{ color: COLORS.gold }}>{formatNumber(stats.networth)}</span>
                        ) : (
                          <span style={{ color: COLORS.text.muted }}>—</span>
                        )}
                      </td>
                      <td style={{ ...cellStyle, fontFamily: 'JetBrains Mono, monospace' }}>
                        <span style={{ color: COLORS.text.secondary }}>{stats.land != null ? formatNumber(stats.land) : '—'}</span>
                      </td>
                      <td style={{ ...cellStyle, fontFamily: 'JetBrains Mono, monospace' }}>
                        <span style={{ color: '#e8a04f' }}>{stats.offensePoints != null ? formatNumber(stats.offensePoints) : '—'}</span>
                      </td>
                      <td style={{ ...cellStyle, fontFamily: 'JetBrains Mono, monospace' }}>
                        <span style={{ color: COLORS.link }}>{stats.defensePoints != null ? formatNumber(stats.defensePoints) : '—'}</span>
                      </td>
                      <td style={cellStyle}>
                        <IntelFreshnessIndicator capturedAt={new Date(group.freshestCapturedAt).toISOString()} />
                      </td>
                    </tr>
                  );
                })}
                {sorted.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ padding: '24px', textAlign: 'center', color: COLORS.text.muted }}>
                      No scouted targets found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', gap: '8px', marginTop: '12px', justifyContent: 'center' }}>
            <button
              onClick={() => onPage(page - 1)}
              disabled={page === 0 || loading}
              style={{ ...paginationStyle, opacity: page === 0 ? 0.4 : 1 }}
            >
              Previous
            </button>
            <span style={{ color: COLORS.text.secondary, fontSize: '12px', alignSelf: 'center' }}>
              Page {page + 1} of {totalPages}
            </span>
            <button
              onClick={() => onPage(page + 1)}
              disabled={(page + 1) * PAGE_SIZE >= totalCount || loading}
              style={{ ...paginationStyle, opacity: (page + 1) * PAGE_SIZE >= totalCount ? 0.4 : 1 }}
            >
              Next
            </button>
          </div>

          {crystalEyeReports.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <div style={{ color: COLORS.text.tertiary, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                Kingdom Events
              </div>
              {crystalEyeReports.map((r) => {
                const data = r.data as { kingdomName?: string; location?: string; events?: { tick: number; summary: string }[] };
                return (
                  <div key={r.id} style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: 12, marginBottom: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text.primary }}>
                      {data.kingdomName ?? r.targetKingdomName} ({data.location ?? r.targetKingdomLocation})
                    </div>
                    {(data.events ?? []).map((e, i) => (
                      <div key={i} style={{ fontSize: 12, color: COLORS.text.secondary, marginTop: 3 }}>
                        {e.summary}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {openTargetId != null && (
        <IntelDetailModal
          reports={openGroup?.reports ?? []}
          onClose={() => setOpenTargetId(null)}
          onShare={handleShare}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: '6px 8px',
  textAlign: 'left',
  color: COLORS.text.tertiary,
  fontWeight: 500,
  fontSize: '11px',
  borderBottom: `1px solid ${COLORS.border}`,
};

const paginationStyle: React.CSSProperties = {
  backgroundColor: COLORS.card,
  border: `1px solid ${COLORS.border}`,
  borderRadius: '4px',
  color: COLORS.text.secondary,
  padding: '4px 12px',
  fontSize: '12px',
  cursor: 'pointer',
};
